import type { Music24PlanTier } from 'fym-shared/src/music24/music24Plans';
import {
  MONTHLY_ADD_ON_MONTHS_PER_YEAR,
  MONTHLY_ADD_ON_SLIDERS,
  MONTHLY_ADD_ON_TIERS,
  type MonthlyAddOnInterval,
  type MonthlyAddOnKind,
  type MonthlyAddOnQuantities,
  extraArtistAddOnMonthlyCents,
  monthlyAddOnCents,
  monthlyAddOnsPeriodCents,
  nextMonthlyAddOnTier,
} from 'fym-shared/src/music24/monthlyAddOns';

/** One tier chip under a slider: the units (first and last, null = no end) and the unit price. */
export interface AddOnTierChip {
  from: number;
  to: number | null;
  unitCents: number;
  /** The tier that the last unit falls in. No chip is on at 0. */
  on: boolean;
}

/** The rows of the section: the two add-ons, and the extra artists of a team plan (design a5). */
export type AddOnRowKind = MonthlyAddOnKind | 'extraArtists';

/** The asked values of the section. `extraArtists` is 0 on a plan with no artists row. */
export interface AddOnDraft extends MonthlyAddOnQuantities {
  extraArtists: number;
}

/** What one slider row shows (design monthly-addons a1, a5). Pure: no React, no intl. */
export interface AddOnRowModel {
  kind: AddOnRowKind;
  quantity: number;
  /** The lowest value (the extra artists in use); 0 for the add-ons. */
  min: number;
  max: number;
  step: number;
  /** Where the value sits on the track, 0 to 100. */
  percent: number;
  /** The hatched part of the track below `min`, 0 to 100 (design a5). */
  floorPercent: number;
  /** The width of each tier zone on the track, in percent (the last zone takes the rest). */
  zonePercents: number[];
  tiers: AddOnTierChip[];
  /** The price of this add-on for one billing period, in US cents. */
  periodCents: number;
  /** The limit with this value; null for no limit. */
  limitWith: number | null;
  /** The price of the next unit and the units left in its tier; null at the end of the slider. */
  next: { unitCents: number; unitsLeftInTier: number | null } | null;
}

/** The tier chips of an add-on with the tier of `quantity` marked. Pure. */
export function addOnTierChips(kind: MonthlyAddOnKind, quantity: number): AddOnTierChip[] {
  let from = 1;
  return MONTHLY_ADD_ON_TIERS[kind].map((tier) => {
    const chip: AddOnTierChip = {
      from,
      to: tier.upTo,
      unitCents: tier.unitCents,
      on: quantity >= from && (tier.upTo === null || quantity <= tier.upTo),
    };
    from = (tier.upTo ?? from) + 1;
    return chip;
  });
}

/** The row of one add-on at `quantity` on top of `baseLimit`. Pure. */
export function addOnRowModel(params: {
  kind: MonthlyAddOnKind;
  quantity: number;
  baseLimit: number | null;
  interval: MonthlyAddOnInterval;
}): AddOnRowModel {
  const { kind, quantity, baseLimit, interval } = params;
  const { max, step } = MONTHLY_ADD_ON_SLIDERS[kind];
  const monthly = monthlyAddOnCents(kind, quantity);
  return {
    kind,
    quantity,
    min: 0,
    max,
    step,
    percent: max === 0 ? 0 : (quantity / max) * 100,
    floorPercent: 0,
    zonePercents: MONTHLY_ADD_ON_TIERS[kind]
      .filter((tier) => tier.upTo !== null)
      .map(
        (tier, index, tiers) => (((tier.upTo ?? 0) - (index === 0 ? 0 : (tiers[index - 1]?.upTo ?? 0))) / max) * 100
      ),
    tiers: addOnTierChips(kind, quantity),
    periodCents: interval === 'yearly' ? monthly * MONTHLY_ADD_ON_MONTHS_PER_YEAR : monthly,
    limitWith: baseLimit === null ? null : baseLimit + quantity,
    next: nextMonthlyAddOnTier(kind, quantity),
  };
}

/**
 * The artists row of a team plan (design a5): a flat price per artist, one chip, no tier zones, and
 * the hatched floor of the extra artists in use. Pure.
 */
export function extraArtistRowModel(params: {
  plan: Music24PlanTier;
  quantity: number;
  min: number;
  max: number;
  step: number;
  /** The artists of the plan with no extra artists. */
  baseLimit: number | null;
  interval: MonthlyAddOnInterval;
}): AddOnRowModel {
  const { plan, quantity, min, max, step, baseLimit, interval } = params;
  const unitCents = extraArtistAddOnMonthlyCents(plan, 1);
  const monthly = extraArtistAddOnMonthlyCents(plan, quantity);
  const percentOf = (value: number) => (max === 0 ? 0 : (Math.min(value, max) / max) * 100);
  return {
    kind: 'extraArtists',
    quantity,
    min,
    max,
    step,
    percent: percentOf(quantity),
    floorPercent: percentOf(min),
    zonePercents: [],
    tiers: [{ from: 1, to: null, unitCents, on: quantity > 0 }],
    periodCents: interval === 'yearly' ? monthly * MONTHLY_ADD_ON_MONTHS_PER_YEAR : monthly,
    limitWith: baseLimit === null ? null : baseLimit + quantity,
    next: null,
  };
}

/** The total of the plan, the add-ons and the extra artists for one period, and its value a month. Pure. */
export function addOnsTotal(params: {
  planPeriodCents: number | null;
  addOns: MonthlyAddOnQuantities;
  interval: MonthlyAddOnInterval;
  /** The extra artists of a team plan; leave out on every other plan. */
  extraArtists?: { plan: Music24PlanTier; quantity: number };
}): { addOnsCents: number; totalCents: number | null; monthlyCents: number | null } {
  const addOnsCents = monthlyAddOnsPeriodCents(params.addOns, params.interval, params.extraArtists);
  const totalCents = params.planPeriodCents === null ? null : params.planPeriodCents + addOnsCents;
  const monthlyCents =
    totalCents === null ? null : Math.round(params.interval === 'yearly' ? totalCents / 12 : totalCents);
  return { addOnsCents, totalCents, monthlyCents };
}

/** How the draft differs from the saved values: a rise, a drop, both or none. Pure. */
export function addOnChange(
  draft: AddOnDraft,
  saved: AddOnDraft
): { changed: boolean; rises: boolean; drops: boolean } {
  const kinds = ['posts', 'videoCredits', 'extraArtists'] as const;
  const rises = kinds.some((kind) => draft[kind] > saved[kind]);
  const drops = kinds.some((kind) => draft[kind] < saved[kind]);
  return { changed: rises || drops, rises, drops };
}

/** The limit of one included artist. An unlimited value (null) adds nothing to a row. Pure. */
export function perArtistShare(limit: number | null, includedArtists: number): number {
  if (limit === null) {
    return 0;
  }
  return Math.round(limit / Math.max(1, includedArtists));
}
